import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import Typography from '@material-ui/core/Typography'
import type { SuggestionData } from 'ckeditor5-premium-features'
import clsx from 'clsx'
import { PROPERTY_EDITOR_PILL_STATE_VARIANT } from 'components/WorkspaceEditor/features/pillSelection'
import { useOptionGroupStyles } from 'components/WorkspaceEditor/styles'
import { getDisabledStyleClasses } from 'components/WorkspaceEditor/utils'
import {
    useShallowSelector
} from 'components/WorkspaceEditor/Workspace2Store'
import React, { memo, useEffect, useMemo } from 'react'
import type { ContentEditorProps } from '../ContentEditor'
import { extractSuggestions } from '../ContentEditor/contentUtils'
import { withLazyLoad } from '../withLazyComponent'
import { useFormatReplacementOption } from './hooks/useFormatReplacementOption'
import { useReplacementOptionController } from './useReplacementOptionController'


/** Props for the ReplacementOption component */
interface ReplacementOptionProps {
    /** The content item containing the text and metadata for the replacement option */
    contentItem: Partial<TextContentElement>

    /** Optional CSS classes to apply custom styling */
    classes?: string | Record<string, string>

    /** Component type to render (for inline usage, use 'span' to avoid nesting warnings) */
    component?: React.ElementType

    /** Whether the replacement option is in an inactive state (disabled) */
    inactive?: boolean

    /** Whether the component is disabled (not hidden, just styled as disabled) */
    disabled?: boolean

    /** Whether the component allows text editing */
    editable?: boolean

    /** Optional React key for list rendering optimization */
    selector?: string

    /**
     * Callback function called when the content value changes.
     * @param updatedItem - The updated content item with new values
     * @param isSelected - Whether the item is currently selected
     */
    onValueChange?: (
        updatedItem: Partial<TextContentElement>,
        isSelected: boolean
    ) => void

    /** Optional maximum character length for the input. When set, input is truncated to this length. */
    maxLength?: number
}

const ContentEditor = withLazyLoad<ContentEditorProps>(() =>
    import('../ContentEditor').then((module) => ({
        default: module.ContentEditor,
    }))
)

/**
 * ReplacementOption Component
 *
 * Renders a replacement option that can display both static and editable content.
 * When editable, it integrates with CKEditor for rich text editing capabilities.
 * Supports Units of Measure handling and maintains consistency with project settings.
 *
 * Features:
 * - Static text display with proper styling
 * - Rich text editing with CKEditor integration
 * - Units of Measure processing and formatting
 * - Focus management and keyboard navigation
 * - Optimistic updates with rollback capabilities
 *
 * @param props - The component props
 * @returns JSX element representing the replacement option
 */
export const ReplacementOption = memo<ReplacementOptionProps>(
    ({
        contentItem,
        classes,
        inactive = false,
        disabled = false,
        editable = false,
        selector,
        onValueChange,
        component = 'p',
        maxLength,
    }) => {
        const {
            editableText,
            editableTextBoxed,
            disabledBoxedInputContainer,
            disabledBoxedInput,
            notDisabledBoxedInputContainer,
            replacementOptionNoBackground,
            defaultCursor,
            disabledOptionGroup,
            disabledPill,
        } = useOptionGroupStyles()
        const {
            setTextContentElementToAdd,
            textContentElementToAdd,
            deactivateAllEditors,
        } = useShallowSelector((state) => ({
            setTextContentElementToAdd:
                state.actions.setTextContentElementToAdd,
            textContentElementToAdd: state.textContentElementToAdd,
            deactivateAllEditors: state.actions.deactivateAllEditors,
        }))
        // Initialize controller for handling editor events and state management
        const { handlers, ckeToggle } = useReplacementOptionController(
            contentItem,
            onValueChange
        )

        // Extract content item ID for tracking and reference
        const contentItemId = contentItem.id || ''

        // Generate unique test ID for component identification in tests
        const testId = `replacement-option-${contentItem.id || 'default'}`

        // Format text content with child elements and UOM handling
        const { formattedText } = useFormatReplacementOption(contentItem)

        // Establish content display priority: formatted text > original text > placeholder text > empty string
        // This ensures we always have displayable content even if formatting fails
        const displayText = formattedText || contentItem.text?.trim() || contentItem.placeholderText || ''

        // For CKEditor, only use actual content (not placeholder) to avoid treating placeholder as real value
        const editorText = formattedText || contentItem.text?.trim() || ''

        // Simple HTML wrapper for CKEditor - no memoization needed for basic string template
        const initialEditorData = `<p>${editorText}</p>`

        // Extract suggestions for track changes functionality
        const initialSuggestions: SuggestionData[] = useMemo(
            () => extractSuggestions([contentItem]),
            [contentItem.id, contentItem.changeType]
        )
        // Extract option item state for disabled styling only
        const isInActiveOptionItem: boolean = useMemo(
            () =>
                contentItem?.optionItemState ===
                PROPERTY_EDITOR_PILL_STATE_VARIANT.InActive,
            [contentItem.optionItemState]
        )

        const { active, placeholderRef, activate } = ckeToggle

        /**
         * Auto-focus effect for replacement options after opted in Replacement option is clicked.
         *
         * When a ReplacementOption is clicked from OptedIn state,
         * this effect automatically activates the editor to allow immediate editing.
         * The state is cleared immediately after activation to prevent stale references.
         *
         * Race condition prevention: Deactivates all other editors before activating this one
         * to ensure only one ReplacementOption is in edit mode at a time.
         */
        useEffect(() => {
            // Defensive null checks: ensure both text and placeholderText are defined
            if (
                contentItem?.text != null &&
                contentItem?.placeholderText != null &&
                contentItem.text !== contentItem.placeholderText &&
                textContentElementToAdd?.id === contentItemId
            ) {
                // CRITICAL: Deactivate all other editors first to prevent multiple editors being active
                deactivateAllEditors()

                // Use requestAnimationFrame to ensure deactivation completes before activation
                requestAnimationFrame(() => {
                    // Activate editor for immediate editing using the raw activate function
                    // Don't use handlers.onFocusCapture here as it expects editor arguments
                    activate()
                })

                // Clear the state immediately after activation
                // This prevents the effect from running again with stale data
                setTextContentElementToAdd(null)
            }
        }, [
            contentItem.text,
            contentItem.placeholderText,
            activate,
            textContentElementToAdd,
            contentItemId,
            setTextContentElementToAdd,
            deactivateAllEditors,
        ])
        /**
         * Determine if the component should be disabled (non-interactive)
         *
         * A replacement option is disabled when any of these conditions are true:
         * - Parent paragraph is inactive (inactive=true)
         * - Component is not editable (editable=false)
         * - Component is explicitly disabled (disabled=true) - used for product groups not added to project
         */
        const isDisabled = inactive || !editable || disabled

        // Get disabled styling classes using shared helper
        const disabledClasses = getDisabledStyleClasses(
            disabled,
            inactive,
            editable,
            isInActiveOptionItem,
            {
                disabledPill,
                disabledBoxedInput,
                disabledOptionGroup,
                disabledBoxedInputContainer,
                notDisabledBoxedInputContainer,
            }
        )

        // Check if we're showing placeholder text (no actual value)
        const isShowingPlaceholder = !formattedText && !contentItem.text?.trim() && !!contentItem.placeholderText


        // Memoize style objects to prevent breaking React.memo optimization
        const divContainerStyle = useMemo(() => ({ display: 'inline-block' as const }), [])

        const placeholderStyle = useMemo(() => ({
            opacity: 0.5,
            display: 'unset !important' as const,
            whiteSpace: 'nowrap' as const,
            padding: '2px 5px'
        }), [])

        const defaultStyle = useMemo(() => ({
            display: 'unset !important' as const,
            whiteSpace: 'nowrap' as const,
            padding: '2px 5px'
        }), [])

        return (
            // This div is necessary to prevent nesting issues with Typography component
            <div style={divContainerStyle}>
                <Typography
                    key={selector || testId}
                    component={component}
                    data-testid={testId}
                    data-suggestion={`content-suggestion-id-${initialSuggestions?.[0]?.id}`}
                    tabIndex={isDisabled || isInActiveOptionItem ? -1 : 0}
                    aria-disabled={isDisabled || isInActiveOptionItem}
                    ref={active ? placeholderRef : null}
                    onFocusCapture={isDisabled ? undefined : () => activate()}
                    onClick={isDisabled ? undefined : () => activate()}
                    className={clsx(
                        classes,
                        editableText,
                        editableTextBoxed,
                        replacementOptionNoBackground,
                        !editable && defaultCursor,
                        disabledClasses
                    )}
                    style={isShowingPlaceholder && !active ? placeholderStyle : defaultStyle}
                    onKeyDown={
                        isDisabled
                            ? undefined
                            : (event: React.KeyboardEvent<HTMLElement>) => handlers.onKeyDown(event)
                    }
                >
                    {active && !isDisabled && editable && !isInActiveOptionItem ? (
                        <ContentEditor
                            initialData={initialEditorData}
                            initialSuggestions={initialSuggestions}
                            handlers={handlers}
                            enableTrackChange={false}
                            maxLength={maxLength}
                        />
                    ) : (
                        displayText
                    )}
                </Typography>
            </div>
        )
    }
)
